import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { filterAdminUsers } from "@/lib/rolePermissions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserFormDialog } from "./UserFormDialog";
import { ApprovedUser, UserFormData, UserRole } from "@/types";
import { Office } from "@/types/office";
import {
  getAllUsers,
  createUser,
  updateUser,
  deactivateUser,
  activateUser,
} from "@/services/userService";
import { getActiveOffices } from "@/services/officeService";
import { useRoles } from "@/hooks/useRoles";
import {
  Search,
  UserPlus,
  Edit,
  UserX,
  UserCheck,
  Loader2,
  Building2,
  Phone,
  Mail,
  Shield,
  X,
  SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

export function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<ApprovedUser[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterOffice, setFilterOffice] = useState<string>("all");
  const [filterRole, setFilterRole] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("active");
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<ApprovedUser | null>(null);
  const [filterExpanded, setFilterExpanded] = useState(false);
  
  const [deactivateDialog, setDeactivateDialog] = useState(false);
  const [activateDialog, setActivateDialog] = useState(false);
  const [actioningUser, setActioningUser] = useState<ApprovedUser | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Fetch roles from Firebase
  const { roles: availableRoles } = useRoles();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersData, officesData] = await Promise.all([
        getAllUsers(),
        getActiveOffices(),
      ]);
      setUsers(usersData);
      setOffices(officesData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load users and offices');
    } finally {
      setLoading(false);
    }
  };

  const getOfficeName = (officeId: string): string => {
    const office = offices.find((o) => o.id === officeId);
    return office?.name || 'Unknown Office';
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setDialogOpen(true);
  };

  const handleEditUser = (user: ApprovedUser) => {
    setEditingUser(user);
    setDialogOpen(true);
  };

  const handleSaveUser = async (formData: UserFormData) => {
    try {
      if (editingUser) {
        await updateUser(editingUser.id, formData);
        toast.success('User updated successfully');
      } else {
        await createUser(formData);
        toast.success('User created successfully');
      }
      await loadData();
    } catch (error) {
      // Error toast is already shown in service
      throw error;
    }
  };

  const handleDeactivateClick = (user: ApprovedUser) => {
    setActioningUser(user);
    setDeactivateDialog(true);
  };

  const handleActivateClick = (user: ApprovedUser) => {
    setActioningUser(user);
    setActivateDialog(true);
  };

  const confirmDeactivate = async () => {
    if (!actioningUser) return;

    setActionLoading(true);
    try {
      await deactivateUser(actioningUser.id);
      toast.success('User deactivated successfully');
      await loadData();
      setDeactivateDialog(false);
      setActioningUser(null);
    } catch (error) {
      console.error('Error deactivating user:', error);
      // Error toast shown in service
    } finally {
      setActionLoading(false);
    }
  };

  const confirmActivate = async () => {
    if (!actioningUser) return;

    setActionLoading(true);
    try {
      await activateUser(actioningUser.id);
      toast.success('User activated successfully');
      await loadData();
      setActivateDialog(false);
      setActioningUser(null);
    } catch (error) {
      console.error('Error activating user:', error);
      // Error toast shown in service
    } finally {
      setActionLoading(false);
    }
  };

  // Filter users based on search and filters
  const activeFilterCount =
    (filterOffice !== "all" ? 1 : 0) +
    (filterRole !== "all" ? 1 : 0) +
    (filterStatus !== "all" ? 1 : 0);

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.phone.includes(searchQuery) ||
      (user.email && user.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (user.employeeId && user.employeeId.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesOffice = filterOffice === "all" || user.officeId === filterOffice;
    
    const matchesRole = filterRole === "all" || user.roles.includes(filterRole as UserRole);
    
    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "active" && user.isActive) ||
      (filterStatus === "inactive" && !user.isActive);

    return matchesSearch && matchesOffice && matchesRole && matchesStatus;
  });

  // Filter out admin users if current user is admin (cannot see other admins)
  // Convert User type to ApprovedUser-compatible format for filtering
  const currentApprovedUser = currentUser ? {
    ...currentUser,
    phone: currentUser.phone || '',
    isActive: true,
  } as ApprovedUser : null;
  const visibleUsers = filterAdminUsers(filteredUsers, currentApprovedUser);

  const getRoleBadgeVariant = (role: UserRole): "default" | "secondary" | "destructive" | "outline" => {
    // Get variant from Firebase role configuration
    const roleConfig = availableRoles.find(r => r.id === role);
    if (roleConfig) {
      return roleConfig.variant as "default" | "secondary" | "destructive" | "outline";
    }
    
    // Fallback to default if role not found
    return 'outline';
  };

  const getRoleLabel = (role: UserRole): string => {
    // Get label from Firebase role configuration
    const roleConfig = availableRoles.find(r => r.id === role);
    return roleConfig?.label || role;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl font-bold flex items-center gap-2 min-w-0">
          <Shield className="h-5 w-5 shrink-0" />
          <span className="truncate">User Management</span>
        </h2>
        <Button
          onClick={handleAddUser}
          size="sm"
          className="shrink-0 gap-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-md hover:shadow-lg transition-all"
        >
          <UserPlus className="h-4 w-4" />
          Add User
        </Button>
      </div>

      {/* Search bar + collapsible filter toggle */}
      <div className="space-y-2">
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, phone, email, or employee ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                type="button"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="relative h-10 px-3 gap-1.5 shrink-0"
            onClick={() => setFilterExpanded(!filterExpanded)}
          >
            <SlidersHorizontal className="h-4 w-4" />
            {activeFilterCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] min-h-[18px] rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-bold leading-none px-0.5">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>

        <AnimatePresence>
          {filterExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="flex flex-wrap gap-2 pb-2 border-b">
                <Select value={filterOffice} onValueChange={setFilterOffice}>
                  <SelectTrigger className="w-[160px] h-9">
                    <SelectValue placeholder="Office" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Offices</SelectItem>
                    {offices.map((office) => (
                      <SelectItem key={office.id} value={office.id}>
                        {office.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterRole} onValueChange={setFilterRole}>
                  <SelectTrigger className="w-[160px] h-9">
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="operations_user">Operations User</SelectItem>
                    <SelectItem value="sales_agent">Sales Agent</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="app_user">App User</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[140px] h-9">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>

                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setFilterOffice("all");
                      setFilterRole("all");
                      setFilterStatus("all");
                    }}
                  >
                    <X className="h-3 w-3 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Results Count */}
      <div className="text-sm text-muted-foreground">
        Showing {visibleUsers.length} of {users.length} users
      </div>

      {/* Mobile View - Cards */}
      <div className="md:hidden space-y-3">
        <AnimatePresence>
          {visibleUsers.map((user) => (
            <motion.div
              key={user.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
            >
              <Card className={`p-4 space-y-3 ${!user.isActive ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <h3 className="font-semibold flex items-center gap-2">
                      {user.name}
                      {!user.isActive && (
                        <Badge variant="outline" className="text-xs">
                          Inactive
                        </Badge>
                      )}
                    </h3>
                    {user.designation && (
                      <p className="text-sm text-muted-foreground">{user.designation}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="h-3 w-3" />
                    {user.phone}
                  </div>
                  {user.email && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-3 w-3" />
                      {user.email}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Building2 className="h-3 w-3" />
                    {getOfficeName(user.officeId)}
                  </div>
                </div>

                <div className="flex flex-wrap gap-1">
                  {user.roles.map((role) => (
                    <Badge key={role} variant={getRoleBadgeVariant(role)} className="text-xs">
                      {getRoleLabel(role)}
                      {role === user.primaryRole && ' ★'}
                    </Badge>
                  ))}
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEditUser(user)}
                    className="flex-1"
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  {user.isActive ? (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeactivateClick(user)}
                      className="flex-1"
                    >
                      <UserX className="h-3 w-3 mr-1" />
                      Deactivate
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => handleActivateClick(user)}
                      className="flex-1"
                    >
                      <UserCheck className="h-3 w-3 mr-1" />
                      Activate
                    </Button>
                  )}
                </div>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>

        {visibleUsers.length === 0 && (
          <Card className="p-8">
            <div className="text-center text-muted-foreground">
              <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No users found matching your filters</p>
            </div>
          </Card>
        )}
      </div>

      {/* Desktop View - Table */}
      <div className="hidden md:block">
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Office</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleUsers.map((user) => (
                <TableRow key={user.id} className={!user.isActive ? 'opacity-60' : ''}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{user.name}</div>
                      {user.designation && (
                        <div className="text-sm text-muted-foreground">{user.designation}</div>
                      )}
                      {user.employeeId && (
                        <div className="text-xs text-muted-foreground">ID: {user.employeeId}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center gap-1 text-sm">
                        <Phone className="h-3 w-3" />
                        {user.phone}
                      </div>
                      {user.email && (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Mail className="h-3 w-3" />
                          {user.email}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      {getOfficeName(user.officeId)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {user.roles.map((role) => (
                        <Badge key={role} variant={getRoleBadgeVariant(role)} className="text-xs">
                          {getRoleLabel(role)}
                          {role === user.primaryRole && ' ★'}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.isActive ? 'default' : 'outline'}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditUser(user)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {user.isActive ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeactivateClick(user)}
                        >
                          <UserX className="h-4 w-4 text-destructive" />
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleActivateClick(user)}
                        >
                          <UserCheck className="h-4 w-4 text-green-600" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {visibleUsers.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">
              <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No users found matching your filters</p>
            </div>
          )}
        </Card>
      </div>

      {/* User Form Dialog */}
      <UserFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        user={editingUser}
        onSave={handleSaveUser}
      />

      {/* Deactivate Confirmation Dialog */}
      <AlertDialog open={deactivateDialog} onOpenChange={setDeactivateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate <strong>{actioningUser?.name}</strong>?
              <br />
              <br />
              This user will no longer be able to access the system. You can reactivate them later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeactivate}
              disabled={actionLoading}
              className="bg-destructive hover:bg-destructive/90"
            >
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Activate Confirmation Dialog */}
      <AlertDialog open={activateDialog} onOpenChange={setActivateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Activate User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to activate <strong>{actioningUser?.name}</strong>?
              <br />
              <br />
              This user will regain access to the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmActivate}
              disabled={actionLoading}
            >
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Activate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
